import React, { useState, useEffect, useCallback, useRef } from 'react';

const ImageGrid = ({ images, onImageClick }) => {
    const [imgStates, setImgStates] = useState({});
    const [visibleImages, setVisibleImages] = useState([]);
    const observerRef = useRef();
    const imageRefs = useRef({});

    useEffect(() => {
        const newStates = {...imgStates};
        images.forEach(img => {
            if (!newStates[img.id]) newStates[img.id] = {loaded: false, error: false};
        });
        setImgStates(newStates);

        if (images.length > 0) {
            setVisibleImages(images.slice(0, 20));
        }
    }, [images]);

    const handleObserver = useCallback(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting && visibleImages.length < images.length) {
                setVisibleImages(prev => {
                    const currentLength = prev.length;
                    const nextBatch = images.slice(currentLength, currentLength + 20);
                    return [...prev, ...nextBatch];
                });
            }
        });
    }, [visibleImages.length, images]);

    useEffect(() => {
        if (images.length > visibleImages.length) {
            observerRef.current = new IntersectionObserver(handleObserver, {
                rootMargin: '100px'
            });

            const lastImageRef = imageRefs.current[`image-${visibleImages.length - 1}`];
            if (lastImageRef) {
                observerRef.current.observe(lastImageRef);
            }
        }

        return () => observerRef.current?.disconnect();
    }, [visibleImages, handleObserver, images.length]);

    if (!images.length) return null;

    return (
        <div className="masonry-gallery">
            {visibleImages.map((img, index) => (
                <div
                    key={img.id}
                    className="gallery-item"
                    onClick={() => onImageClick(images.findIndex(image => image.id === img.id))}
                    ref={el => imageRefs.current[`image-${index}`] = el}
                >
                    <div className="gallery-item-inner">
                        {!imgStates[img.id]?.loaded && <div className="spinner"></div>}

                        {imgStates[img.id]?.error ? (
                            <div className="image-error"><i className="fa fa-image"></i></div>
                        ) : (
                            <img
                                src={img.thumbnail_url || img.image_url}
                                alt=""
                                loading="lazy"
                                className="gallery-image"
                                style={{opacity: imgStates[img.id]?.loaded ? 1 : 0}}
                                onLoad={() => setImgStates(prev => ({...prev, [img.id]: {...prev[img.id], loaded: true}}))}
                                onError={() => setImgStates(prev => ({...prev, [img.id]: {loaded: true, error: true}}))}
                            />
                        )}
                    </div>
                </div>
            ))}

            {visibleImages.length < images.length && (
                <div className="load-more-indicator">
                    <div className="spinner"></div>
                </div>
            )}
        </div>
    );
};

export default ImageGrid;